import { useEffect, useMemo, useState } from "react";
import { ControllerFieldState, ControllerRenderProps } from "react-hook-form";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useDebouncedValue } from "../../../../../hooks/useDebounce";
import { useOrganProfileList } from "../../../../../services/api-tansquery/organ-profile-list";
import { useOrgao } from "../../../../../contexts/orgao";
import { useAuth } from "../../../../../contexts/auth";
import { useUserOrgaos } from "../../../../../services/api-tansquery/user/user-orgaos";
import { Totem } from "../../../../../@types/totem";

type Option = {
    label: string;
    value: string;
}

type Props = {
    field: ControllerRenderProps<{
        descricao?: string;
        cor_fonte?: string;
        cor_fundo?: string;
        local_atendimento?: string;
        orgao?: string;
        servicos?: string[];
        prioridades?: string[];
        logo?: File | string;
    }, "orgao">;
    fieldState: ControllerFieldState;
    totem?: Totem;
    style?: React.CSSProperties;
    /** Quando true, exibe apenas o órgão selecionado no seletor do topo da página (ex: na criação de totem) */
    restrictToCurrentOrgao?: boolean;
}

export default function OrganField({ field, fieldState, style, totem, restrictToCurrentOrgao }: Props) {
    const [search, setSearch] = useState<string>();
    const debouncedSearch = useDebouncedValue(search, 500);
    const { currentOrgaoId } = useOrgao();
    const { adminUser } = useAuth();
    const { data: userOrgaosData } = useUserOrgaos();
    const { data, isLoading } = useOrganProfileList(
        restrictToCurrentOrgao ? { page: 1 } : { search: debouncedSearch, page: 1 }
    );

    const userOrgaosFromAPI = userOrgaosData?.user_orgaos || [];
    const userOrgaosFromAdminUser = adminUser?.orgaos?.map(item => ({
        id: item.orgao.id,
        nome: item.orgao.nome,
        sigla: item.orgao.sigla,
    })) || [];
    const allOrgaos = [...userOrgaosFromAPI, ...userOrgaosFromAdminUser];
    const userOrgaos = Array.from(
        new Map(allOrgaos.map(item => [item.id, item])).values()
    );
    const currentOrgao = useMemo(() => {
        if (!restrictToCurrentOrgao) return null;
        if (currentOrgaoId) {
            return userOrgaos.find(org => org.id === currentOrgaoId) ?? null;
        }
        return adminUser?.orgao_usuario || adminUser?.orgaos?.[0]?.orgao || userOrgaos[0] || null;
    }, [restrictToCurrentOrgao, currentOrgaoId, userOrgaos, adminUser]);

    const organList = useMemo(() => {
        return data?.results || [];
    }, [data]);

    const options = useMemo((): Option[] => {
        if (restrictToCurrentOrgao && currentOrgao) {
            return [{
                label: currentOrgao.nome,
                value: currentOrgao.id.toString(),
            }];
        }

        const merged = organList.map(item => ({
            label: item.nome,
            value: item.id.toString(),
        }));

        if (totem?.orgao) {
            merged.push({
                label: totem.orgao.nome,
                value: totem.orgao.id.toString(),
            });
        }

        return merged.filter(
            (item, index, self) => self.findIndex(
                i => i.value === item.value
            ) === index
        );
    }, [restrictToCurrentOrgao, currentOrgao, organList, totem]);

    useEffect(() => {
        if (restrictToCurrentOrgao && currentOrgao && !field.value) {
            field.onChange(currentOrgao.id.toString());
        }
    }, [restrictToCurrentOrgao, currentOrgao, field]);

    function handleInputChange(_, value: string, reason: string) {
        if (reason === "input") {
            setSearch(value.trim() === "" ? undefined : value);
        }
    }

    function handleSelectChange(_, newValue: Option | null) {
        if (!newValue) {
            field.onChange("");
        } else {
            field.onChange(newValue.value);
        }
    }

    const isLoadingField = restrictToCurrentOrgao ? !currentOrgao : isLoading;

    return (
        <Autocomplete
            style={style}
            freeSolo={false}
            options={options}
            value={options?.find((item) => item.value === field.value) || null}
            getOptionLabel={(option) => option.label}
            getOptionSelected={(option, value) => option.value === value.value}
            onChange={handleSelectChange}
            onInputChange={restrictToCurrentOrgao ? undefined : handleInputChange}
            loading={isLoadingField}
            loadingText="Carregando órgãos..."
            noOptionsText="Nenhum órgão encontrado"
            disableClearable={restrictToCurrentOrgao}
            renderInput={(params) => (
                <TextField
                    {...params}
                    required
                    label="Órgão"
                    variant="outlined"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || "Selecione o órgão responsável"}
                    InputProps={{
                        ...params.InputProps,
                        required: false,
                        readOnly: restrictToCurrentOrgao,
                        endAdornment: (
                            <>
                                {isLoadingField ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
